// The Diversify card's figure rules -- Beginning and Current Concentration,
// whole-bucket and per stock. Pure, so the rules are testable without the
// report; useDiversifyCard holds the state they read.
import { DiversifyThemedCardValues, ReportValueCardProps } from "helpers/types";
import { DIVERSIFY_ALL_STOCKS_OPTION } from "helpers/constants";
import { toFiniteNumber } from "components/ui/formatters";
import { KeepFiguresState } from "./keepFigures";
import { NO_REPORT_DISPLAY } from "../reportFieldStates";

/**
 * Beginning Concentration's All Stocks sentence when a run carried no
 * Bucket 1 data: the 100% baseline is a share OF Bucket 1's stock, so with no
 * stock there is nothing for it to be a share of.
 */
export const DIVERSIFY_NO_BUCKET_1 =
  "No figure — this run produced no Bucket 1 results, so there is no " +
  "concentrated stock to measure a beginning concentration against.";

/**
 * What the Diversify card's All Stocks Beginning Concentration row shows
 * (operator, task 4848).
 *
 * The 100% baseline is a definition, but showing it before any report has
 * run put a figure-weight 100.00% on a page whose every other row says
 * "No Report" -- so the baseline waits for a stored result like its
 * neighbours do. `hasStoredReport` is "a completed run has stored figures",
 * read from the same persisted inputs key the PDF button reads, so it holds
 * across a page reload exactly as the localStorage-backed rows' figures do.
 *
 * A TYPED figure outranks every state, as an edited Keep row does: the reader
 * may type over "No Report" before running, and the run sends no figure for
 * this row, so what they typed stands. A run that carried no Bucket 1 data
 * gets the same muted N/A the Keep card does, because a baseline share of a
 * stock the run did not find would be a figure about nothing.
 */
export const beginningConcentrationFigure = (
  baseline: number | string,
  typed: boolean,
  keepState: KeepFiguresState,
  hasStoredReport: boolean,
): Pick<ReportValueCardProps, "value" | "unavailableReason"> => {
  if (typed) return { value: baseline };
  if (keepState === "absent")
    return { value: "", unavailableReason: DIVERSIFY_NO_BUCKET_1 };
  if (keepState === "figures" || hasStoredReport) return { value: baseline };
  return { value: NO_REPORT_DISPLAY };
};

/**
 * What the Diversify card's Current Concentration shows, in the two places
 * that show it: the ROW, and the allocation donut beside it.
 *
 * ONE function for both, and that is the whole reason it exists. The row and
 * the donut have to agree about whether this run produced a figure at all,
 * and the rule expressed twice is how a row reading N/A ends up next to a
 * donut still drawing a complete ring from the PREVIOUS run's stored
 * concentration -- two answers to one question, on one card. `keepRowFigure`
 * (keepFigures) is the same pattern for the Keep card's eight rows.
 *
 * `stored` is the persisted figure (or, before any run, the "No Report"
 * seed); `reason` is the sentence the backend sent when it reached the figure
 * and refused it (build_diversify_card holds the three it can send).
 *
 * A refusal returns the EMPTY SENTINEL with the reason -- which is what
 * MultiValuesCard renders as a muted "N/A" behind the info icon and what
 * toPdfValues drops from the client-facing export -- and `pct: null`, which
 * is what stops both donuts drawing. The stored figure is deliberately left
 * alone underneath: a refusal must not destroy a hand-entered number, it
 * must only stop it passing for this run's answer.
 */
export const currentConcentrationFigure = (
  stored: number | string,
  reason?: string,
): Pick<ReportValueCardProps, "value" | "unavailableReason"> & {
  pct: number | null;
} =>
  reason
    ? { value: "", unavailableReason: reason, pct: null }
    : { value: stored, pct: toFiniteNumber(stored) };

/**
 * The four fields the Diversify card's stock dropdown reads, lifted out of
 * the run's payload so the resolution below can be tested without one.
 */
export type DiversifyStockConcentrations = Pick<
  DiversifyThemedCardValues,
  | "current_concentration_by_symbol"
  | "current_concentration_by_symbol_unavailable"
  | "beginning_concentration_by_symbol"
  | "beginning_concentration_by_symbol_unavailable"
>;

/**
 * What the Diversify card's two concentration rows show for ONE selected
 * Bucket 1 stock (operator, task 3508) -- the figure, or the empty
 * sentinel behind the sentence saying why this run has none.
 *
 * ONE function for both rows, and the same pair every refused figure on
 * these cards renders: a muted "N/A" behind an info icon rather than a
 * number nobody calculated. The two rows refuse on their OWN terms and so
 * read their own reason key -- a run can price the whole portfolio today
 * and still have opened Bucket 1 at nothing, which blanks the opening row
 * while today's share stays perfectly measurable.
 *
 * A SYMBOL THE PAYLOAD DOES NOT CARRY is the third state and gets the
 * empty sentinel with no sentence: the map is keyed by the stocks this run
 * produced, so a symbol missing from it was never measured. It is
 * unreachable while the dropdown is built from the same run's stocks, and
 * it is here so that a payload and a picker that disagree cannot put the
 * PREVIOUS selection's number under this one's name.
 *
 * Note what this never returns: the All Stocks selection. That row is the
 * reader's own 100% baseline (useBeginningConcentration) and the card's
 * stored whole-of-Bucket-1 concentration, neither of which is this
 * payload's to serve -- the caller keeps those two on their own path.
 */
export const diversifyStockFigure = (
  bySymbol: Record<string, number> | null,
  reason: string | null,
  symbol: string,
): Pick<ReportValueCardProps, "value" | "unavailableReason"> => {
  if (reason) return { value: "", unavailableReason: reason };
  const figure = bySymbol?.[symbol];
  // `== null` and not `!`, so a genuine 0% -- a client who has left this
  // stock entirely -- is a result rather than a blank.
  return figure == null ? { value: "" } : { value: figure };
};

/**
 * A concentration row's label with the stock it was measured for appended
 * (operator, task 3508): "Current Concentration: AAPL".
 *
 * The All Stocks selection keeps the plain label, because that IS what the
 * row has always meant -- a suffix there would suggest a narrowing that
 * has not happened.
 */
export const concentrationRowLabel = (label: string, symbol: string): string =>
  symbol === DIVERSIFY_ALL_STOCKS_OPTION ? label : `${label}: ${symbol}`;

/**
 * The four per-stock fields lifted off a run's Diversify payload, each
 * normalised to null when the payload does not carry it -- or null for the
 * whole when the run carried no Diversify card at all.
 */
export const diversifyStockConcentrations = (
  diversify: DiversifyThemedCardValues | undefined,
): DiversifyStockConcentrations | null =>
  diversify
    ? {
        current_concentration_by_symbol:
          diversify.current_concentration_by_symbol ?? null,
        current_concentration_by_symbol_unavailable:
          diversify.current_concentration_by_symbol_unavailable ?? null,
        beginning_concentration_by_symbol:
          diversify.beginning_concentration_by_symbol ?? null,
        beginning_concentration_by_symbol_unavailable:
          diversify.beginning_concentration_by_symbol_unavailable ?? null,
      }
    : null;
